import "../styles/global.scss";
import "@fortawesome/fontawesome-svg-core/styles.css";
import React, { ContextType, useMemo } from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { IntlProvider } from "react-intl";
import { Toaster } from "react-hot-toast";
import { config } from "@fortawesome/fontawesome-svg-core";
import ApplicationContext, {
  AnalyticsState,
  defaultAnalyticsState,
  LocalizationState,
} from "@/contexts/ApplicationContext";
import English from "../i18n/compiled-locales/en.json";
import { getInitialLanguage } from "../hooks/useInitialLanguage";
import { useStorage } from "../hooks/useStorage";
import YupLocalization from "../contexts/YupLocalization";

// https://docs.fontawesome.com/web/use-with/react/use-with
config.autoAddCss = false;

const cache = createCache({ key: "next" });

const App = ({ Component, pageProps }: { Component: React.ComponentType; pageProps: object }) => {
  const [localizationState, setLocalizationState] = useStorage<LocalizationState>(
    global?.localStorage,
    "localization",
    () => ({
      locale: getInitialLanguage(),
    }),
  );

  const [analyticsState, setAnalyticsState] = useStorage<AnalyticsState>(
    global?.localStorage,
    "analytics",
    () => defaultAnalyticsState,
  );

  const applicationContext = useMemo<ContextType<typeof ApplicationContext>>(
    () => ({ setLocalizationState, analytics: analyticsState, setAnalyticsState }),
    [setLocalizationState, analyticsState, setAnalyticsState],
  );

  const messages = useMemo(() => {
    switch (localizationState.locale) {
      default:
        return English;
    }
  }, [localizationState.locale]);

  return (
    <CacheProvider value={cache}>
      <IntlProvider locale={localizationState.locale} messages={messages}>
        <ApplicationContext.Provider value={applicationContext}>
          <YupLocalization>
            <Component {...pageProps} />
            <Toaster
              toastOptions={{
                position: "bottom-right",
                duration: 5000,
              }}
            />
          </YupLocalization>
        </ApplicationContext.Provider>
      </IntlProvider>
    </CacheProvider>
  );
};

export default App;
